// DashboardTab.js
// Extracted from AIWorkforcePage.js for better maintainability

import { formatDistanceToNow } from 'date-fns';
import { TabsContent } from '../../../components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Avatar, AvatarFallback } from '../../../components/ui/avatar';
import { Progress } from '../../../components/ui/progress';
import { 
  Bot, Zap, CheckCircle, Clock, XCircle, X, TrendingUp, 
  Activity, ChevronDown, Users 
} from 'lucide-react';
import { TASK_STATUS_COLORS } from '../constants';

/**
 * Dashboard tab component for AI Workforce overview
 * 
 * @param {Object} props
 * @param {Object} props.dashboardData - Dashboard statistics and performance data
 * @param {string|null} props.dashboardFilter - Current active filter (active_agents, in_progress, etc.)
 * @param {function} props.onDashboardFilterChange - Callback when filter changes
 * @param {Array} props.agents - List of AI agents
 * @param {Array} props.failedTasks - List of failed tasks
 * @param {Object} props.collapsedSections - Object tracking collapsed section states
 * @param {function} props.onToggleSection - Callback to toggle a section
 * @param {function} props.onNavigateToQueue - Callback to navigate to queue tab
 */
export const DashboardTab = ({
  dashboardData,
  dashboardFilter,
  onDashboardFilterChange,
  agents = [],
  failedTasks = [],
  collapsedSections = {},
  onToggleSection,
  onNavigateToQueue
}) => {
  return (
    <TabsContent value="dashboard" className="space-y-6">
      {/* Summary Cards - Clickable */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card 
          className={`bg-gradient-to-br from-violet-50 to-purple-50 border-violet-100 cursor-pointer transition-all hover:shadow-md ${dashboardFilter === 'active_agents' ? 'ring-2 ring-violet-500' : ''}`}
          onClick={() => onDashboardFilterChange(dashboardFilter === 'active_agents' ? null : 'active_agents')}
          data-testid="dashboard-card-active-agents"
        >
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-4xl font-bold text-violet-700">{dashboardData?.agents?.active || 0}</p>
                <p className="text-sm text-violet-600">Active Agents</p>
              </div>
              <Bot className="h-10 w-10 text-violet-300" />
            </div>
            <p className="text-xs text-muted-foreground mt-2">{dashboardData?.agents?.total || 0} total agents</p>
          </CardContent>
        </Card>

        <Card 
          className={`bg-gradient-to-br from-blue-50 to-cyan-50 border-blue-100 cursor-pointer transition-all hover:shadow-md ${dashboardFilter === 'in_progress' ? 'ring-2 ring-blue-500' : ''}`}
          onClick={() => onDashboardFilterChange(dashboardFilter === 'in_progress' ? null : 'in_progress')}
          data-testid="dashboard-card-in-progress"
        >
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-4xl font-bold text-blue-700">{dashboardData?.tasks?.in_progress || 0}</p>
                <p className="text-sm text-blue-600">Tasks In Progress</p>
              </div>
              <Zap className="h-10 w-10 text-blue-300" />
            </div>
            <p className="text-xs text-muted-foreground mt-2">{dashboardData?.tasks?.pending || 0} pending</p>
          </CardContent>
        </Card>

        <Card 
          className={`bg-gradient-to-br from-emerald-50 to-green-50 border-emerald-100 cursor-pointer transition-all hover:shadow-md ${dashboardFilter === 'completed_today' ? 'ring-2 ring-emerald-500' : ''}`}
          onClick={() => onDashboardFilterChange(dashboardFilter === 'completed_today' ? null : 'completed_today')}
          data-testid="dashboard-card-completed-today"
        >
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-4xl font-bold text-emerald-700">{dashboardData?.tasks?.completed_today || 0}</p>
                <p className="text-sm text-emerald-600">Completed Today</p>
              </div>
              <CheckCircle className="h-10 w-10 text-emerald-300" />
            </div>
            <p className="text-xs text-muted-foreground mt-2">{dashboardData?.tasks?.failed_today || 0} failed</p>
          </CardContent>
        </Card>

        <Card 
          className={`bg-gradient-to-br from-amber-50 to-orange-50 border-amber-100 cursor-pointer transition-all hover:shadow-md ${dashboardFilter === 'awaiting_approval' ? 'ring-2 ring-amber-500' : ''}`}
          onClick={() => onDashboardFilterChange(dashboardFilter === 'awaiting_approval' ? null : 'awaiting_approval')}
          data-testid="dashboard-card-awaiting-approval"
        >
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-4xl font-bold text-amber-700">{dashboardData?.tasks?.awaiting_approval || 0}</p>
                <p className="text-sm text-amber-600">Awaiting Approval</p>
              </div>
              <Clock className="h-10 w-10 text-amber-300" />
            </div>
            <p className="text-xs text-muted-foreground mt-2">{dashboardData?.tasks?.sla_breached || 0} SLA breached</p>
          </CardContent>
        </Card>

        <Card 
          className={`bg-gradient-to-br from-red-50 to-rose-50 border-red-100 cursor-pointer transition-all hover:shadow-md ${dashboardFilter === 'failed' ? 'ring-2 ring-red-500' : ''}`}
          onClick={() => onDashboardFilterChange(dashboardFilter === 'failed' ? null : 'failed')}
          data-testid="dashboard-card-failed"
        >
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-4xl font-bold text-red-700">{dashboardData?.tasks?.failed_today || 0}</p>
                <p className="text-sm text-red-600">Tasks Failed</p>
              </div>
              <XCircle className="h-10 w-10 text-red-300" />
            </div>
            <p className="text-xs text-muted-foreground mt-2">Click to view details</p>
          </CardContent>
        </Card>
      </div>

      {/* Filtered Results Panel */}
      {dashboardFilter && (
        <Card className="border-2 border-dashed">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-lg">
                {dashboardFilter === 'active_agents' && <><Bot className="h-5 w-5 text-violet-600" /> Active Agents</>}
                {dashboardFilter === 'in_progress' && <><Zap className="h-5 w-5 text-blue-600" /> Tasks In Progress</>}
                {dashboardFilter === 'completed_today' && <><CheckCircle className="h-5 w-5 text-emerald-600" /> Completed Today</>}
                {dashboardFilter === 'awaiting_approval' && <><Clock className="h-5 w-5 text-amber-600" /> Awaiting Approval</>}
                {dashboardFilter === 'failed' && <><XCircle className="h-5 w-5 text-red-600" /> Failed Tasks</>}
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => onDashboardFilterChange(null)}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {dashboardFilter === 'active_agents' && (
              <div className="space-y-2">
                {agents.filter(a => a.status === 'active').map(agent => (
                  <div key={agent.id} className="flex items-center justify-between p-3 bg-violet-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Avatar className="h-8 w-8">
                        <AvatarFallback className="bg-violet-200 text-violet-700">{agent.name[0]}</AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-medium">{agent.name}</p>
                        <p className="text-xs text-muted-foreground">{agent.role_title}</p>
                      </div>
                    </div>
                    <Badge className="bg-violet-100 text-violet-700">{agent.active_tasks || 0} active tasks</Badge>
                  </div>
                ))}
                {agents.filter(a => a.status === 'active').length === 0 && (
                  <p className="text-center text-muted-foreground py-4">No active agents</p>
                )}
              </div>
            )}
            {dashboardFilter === 'in_progress' && (
              <div className="space-y-2">
                {dashboardData?.recent_tasks?.filter(t => t.status === 'in_progress').map(task => (
                  <div key={task.id} className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                    <div>
                      <p className="font-medium">{task.title}</p>
                      <p className="text-xs text-muted-foreground">{task.assigned_agent_name || 'Unassigned'}</p>
                    </div>
                    <span className="text-xs text-blue-600">{formatDistanceToNow(new Date(task.created_at), { addSuffix: true })}</span>
                  </div>
                ))}
                {(!dashboardData?.recent_tasks?.filter(t => t.status === 'in_progress').length) && (
                  <p className="text-center text-muted-foreground py-4">No tasks in progress</p>
                )}
              </div>
            )}
            {dashboardFilter === 'completed_today' && (
              <div className="space-y-2">
                {dashboardData?.recent_tasks?.filter(t => t.status === 'completed').map(task => (
                  <div key={task.id} className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg">
                    <div>
                      <p className="font-medium">{task.title}</p>
                      <p className="text-xs text-muted-foreground">{task.assigned_agent_name || 'Human completed'}</p>
                    </div>
                    <CheckCircle className="h-4 w-4 text-emerald-600" />
                  </div>
                ))}
                {(!dashboardData?.recent_tasks?.filter(t => t.status === 'completed').length) && (
                  <p className="text-center text-muted-foreground py-4">No tasks completed today</p>
                )}
              </div>
            )}
            {dashboardFilter === 'awaiting_approval' && (
              <div className="space-y-2">
                {dashboardData?.recent_tasks?.filter(t => t.status === 'awaiting_approval').map(task => (
                  <div key={task.id} className="flex items-center justify-between p-3 bg-amber-50 rounded-lg">
                    <div>
                      <p className="font-medium">{task.title}</p>
                      <p className="text-xs text-muted-foreground">{task.assigned_agent_name || 'Unassigned'}</p>
                    </div>
                    <Button size="sm" variant="outline" className="border-amber-300 text-amber-700">
                      Review
                    </Button>
                  </div>
                ))}
                {(!dashboardData?.recent_tasks?.filter(t => t.status === 'awaiting_approval').length) && (
                  <p className="text-center text-muted-foreground py-4">No tasks awaiting approval</p>
                )}
              </div>
            )}
            {dashboardFilter === 'failed' && (
              <div className="space-y-2">
                {failedTasks.length > 0 ? failedTasks.map(task => (
                  <div key={task.id} className="p-3 bg-red-50 rounded-lg border border-red-200">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="font-medium text-red-800">{task.title}</p>
                        <p className="text-xs text-muted-foreground mt-1">{task.assigned_agent_name || 'Unassigned'}</p>
                        {task.error_message && (
                          <div className="mt-2 p-2 bg-red-100 rounded text-xs text-red-700 font-mono">
                            {task.error_message}
                          </div>
                        )}
                        <p className="text-xs text-red-600 mt-2">
                          Failed {formatDistanceToNow(new Date(task.updated_at || task.created_at), { addSuffix: true })}
                        </p>
                      </div>
                      <XCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
                    </div>
                  </div>
                )) : (
                  <p className="text-center text-muted-foreground py-4">No failed tasks</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Agent Performance - Collapsible */}
      <Card>
        <CardHeader 
          className="cursor-pointer hover:bg-slate-50 transition-colors rounded-t-lg"
          onClick={() => onToggleSection('agentPerformance')}
        >
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Agent Performance (24h)
            </CardTitle>
            <ChevronDown className={`h-5 w-5 transition-transform ${collapsedSections.agentPerformance ? '-rotate-90' : ''}`} />
          </div>
        </CardHeader>
        {!collapsedSections.agentPerformance && (
          <CardContent>
            <div className="space-y-4">
              {dashboardData?.agent_performance?.map((agent, idx) => (
                <div key={idx} className="flex items-center gap-4">
                  <Avatar className="h-10 w-10">
                    <AvatarFallback className="bg-violet-100 text-violet-700">{agent.name[0]}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <div>
                        <span className="font-medium">{agent.name}</span>
                        <span className="text-sm text-muted-foreground ml-2">{agent.role_title}</span>
                      </div>
                      <span className="text-sm font-medium">{agent.success_rate}% success</span>
                    </div>
                    <Progress value={agent.success_rate} className="h-2" />
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-emerald-600">{agent.completed_24h} completed</p>
                    {agent.failed_24h > 0 && (
                      <p className="text-xs text-red-600">{agent.failed_24h} failed</p>
                    )}
                  </div>
                </div>
              ))}
              {(!dashboardData?.agent_performance || dashboardData.agent_performance.length === 0) && (
                <p className="text-center text-muted-foreground py-4">No agent activity yet</p>
              )}
            </div>
          </CardContent>
        )}
      </Card>

      {/* Recent Tasks - Collapsible */}
      <Card>
        <CardHeader 
          className="cursor-pointer hover:bg-slate-50 transition-colors rounded-t-lg"
          onClick={() => onToggleSection('recentTasks')}
        >
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5" />
              Recent Tasks
            </CardTitle>
            <ChevronDown className={`h-5 w-5 transition-transform ${collapsedSections.recentTasks ? '-rotate-90' : ''}`} />
          </div>
        </CardHeader>
        {!collapsedSections.recentTasks && (
          <CardContent>
            <div className="space-y-3">
              {dashboardData?.recent_tasks?.map(task => (
                <div 
                  key={task.id} 
                  className="flex items-center justify-between p-3 border rounded-lg hover:bg-slate-50 cursor-pointer" 
                  onClick={() => onNavigateToQueue && onNavigateToQueue()}
                >
                  <div className="flex items-center gap-3">
                    {task.assigned_agent_name ? (
                      <div className="h-8 w-8 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center" title="AI Task">
                        <Bot className="h-4 w-4 text-white" />
                      </div>
                    ) : (
                      <div className="h-8 w-8 rounded-full bg-slate-200 flex items-center justify-center" title="Human Task">
                        <Users className="h-4 w-4 text-slate-600" />
                      </div>
                    )}
                    <div>
                      <div className="flex items-center gap-2">
                        <Badge className={TASK_STATUS_COLORS[task.status] || 'bg-slate-100'}>{task.status}</Badge>
                        <span className="font-medium">{task.title}</span>
                      </div>
                      {task.assigned_agent_name && (
                        <p className="text-xs text-violet-600 flex items-center gap-1 mt-0.5">
                          <Bot className="h-3 w-3" /> {task.assigned_agent_name}
                        </p>
                      )}
                    </div>
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {formatDistanceToNow(new Date(task.created_at), { addSuffix: true })}
                  </span>
                </div>
              ))}
              {(!dashboardData?.recent_tasks || dashboardData.recent_tasks.length === 0) && (
                <p className="text-center text-muted-foreground py-4">No recent tasks</p>
              )}
            </div>
          </CardContent>
        )}
      </Card>
    </TabsContent>
  );
};

export default DashboardTab;
